import { useEffect, useState } from 'react';
import {
  Save, ExternalLink, FileText, Plus, Trash2, X, ChevronUp, ChevronDown,
  Phone, Mail, MessageCircle, Store, Package, CheckCircle2, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardHeader, CardTitle, CardContent, Input, useConfirm } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { TextField, TextArea } from '@/components/shared/CmsFields';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('info-pages');

type ContactIcon = 'phone' | 'mail' | 'message-circle' | 'store';
type ReturnsIcon = 'package' | 'check' | 'truck';

interface InfoSection { heading: string; body: string }
interface LegalContent {
  title: string;
  intro: string;
  lastUpdated: string;
  sections: InfoSection[];
  footerNote?: string;
}
interface ContactChannel { icon: ContactIcon; label: string; value: string; sub: string; href: string }
interface ContactHours { days: string; time: string }
interface ContactContent {
  eyebrow: string;
  title: string;
  description: string;
  channels: ContactChannel[];
  formHeading: string;
  formIntro: string;
  formSubjects: string[];
  officeHeading: string;
  officeLines: string[];
  hoursHeading: string;
  hours: ContactHours[];
}
interface ReturnsStep { icon: ReturnsIcon; label: string; desc: string }
interface ReturnsContent {
  title: string;
  intro: string;
  windowBadge: string;
  windowSub: string;
  stepsHeading: string;
  steps: ReturnsStep[];
  nonReturnableHeading: string;
  nonReturnableIntro: string;
  nonReturnable: string[];
  ctaHeading: string;
  ctaSub: string;
  ctaLabel: string;
  ctaHref: string;
}
interface InfoPagesContent {
  contact: ContactContent;
  privacy: LegalContent;
  returns: ReturnsContent;
  terms: LegalContent;
}

const CONTACT_ICONS: { value: ContactIcon; Icon: React.ElementType; label: string }[] = [
  { value: 'phone',           Icon: Phone,         label: 'Phone' },
  { value: 'mail',            Icon: Mail,          label: 'Mail' },
  { value: 'message-circle',  Icon: MessageCircle, label: 'Chat' },
  { value: 'store',           Icon: Store,         label: 'Store' },
];

const RETURNS_ICONS: { value: ReturnsIcon; Icon: React.ElementType; label: string }[] = [
  { value: 'package', Icon: Package,       label: 'Package' },
  { value: 'check',   Icon: CheckCircle2,  label: 'Check' },
  { value: 'truck',   Icon: Truck,         label: 'Truck' },
];

// ─── Legal editor (Privacy/Terms) ──────────────────────────────────────────

function LegalEditor({ value, onChange }: { value: LegalContent; onChange: (next: LegalContent) => void }) {
  const confirm = useConfirm();
  const set = <K extends keyof LegalContent>(k: K, v: LegalContent[K]) => onChange({ ...value, [k]: v });

  const updateSection = (i: number, patch: Partial<InfoSection>) =>
    set('sections', value.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeSection = async (i: number) => {
    if (!(await confirm({
      title: 'Remove this section?',
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return;
    set('sections', value.sections.filter((_, idx) => idx !== i));
  };
  const moveSection = (from: number, to: number) => {
    if (to < 0 || to >= value.sections.length) return;
    const next = [...value.sections];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    set('sections', next);
  };
  const addSection = () => set('sections', [...value.sections, { heading: 'New section', body: '' }]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Page header</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Title" value={value.title} onChange={(v) => set('title', v)} />
          <TextArea label="Intro paragraph" value={value.intro} onChange={(v) => set('intro', v)} rows={2} />
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Last updated" value={value.lastUpdated} onChange={(v) => set('lastUpdated', v)} placeholder="May 16, 2026" />
            <TextField label="Footer note (optional)" value={value.footerNote ?? ''} onChange={(v) => set('footerNote', v)} placeholder="Questions? Contact us." />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary-600" />
            Sections <span className="text-xs font-normal text-slate-500">({value.sections.length})</span>
          </CardTitle>
          <Button size="sm" onClick={addSection}><Plus className="h-4 w-4" /> Add section</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {value.sections.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-500">No sections yet.</p>
          )}
          {value.sections.map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={s.heading} onChange={(e) => updateSection(i, { heading: e.target.value })}
                  placeholder="Section heading" className="h-9 font-semibold flex-1" />
                <button type="button" onClick={() => moveSection(i, i - 1)} disabled={i === 0}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => moveSection(i, i + 1)} disabled={i === value.sections.length - 1}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => removeSection(i)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <textarea value={s.body} onChange={(e) => updateSection(i, { body: e.target.value })} rows={3}
                placeholder="Body. Separate paragraphs with a blank line."
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-y" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Contact editor ────────────────────────────────────────────────────────

function ContactEditor({ value, onChange }: { value: ContactContent; onChange: (next: ContactContent) => void }) {
  const set = <K extends keyof ContactContent>(k: K, v: ContactContent[K]) => onChange({ ...value, [k]: v });

  // Auto-derive the link target from the channel type so the admin never has
  // to think about tel: / mailto: prefixes. For "store" / "message-circle" the
  // value itself may be a URL, so we pass it through as long as it looks like one.
  const hrefFor = (icon: ContactIcon, raw: string): string => {
    const v = (raw || '').trim();
    if (!v) return '';
    switch (icon) {
      case 'phone':
        return `tel:${v.replace(/\s+/g, '')}`;
      case 'mail':
        return `mailto:${v}`;
      case 'message-circle':
        // value is typically a phone number — open WhatsApp's wa.me link
        return `https://wa.me/${v.replace(/[^\d]/g, '')}`;
      case 'store':
      default:
        return /^https?:\/\//i.test(v) ? v : v;
    }
  };

  const updateChannel = (i: number, patch: Partial<ContactChannel>) => {
    const current = value.channels[i];
    const next = { ...current, ...patch };
    // Whenever icon or value change, re-derive href so it stays consistent.
    if (patch.icon !== undefined || patch.value !== undefined) {
      next.href = hrefFor(next.icon, next.value);
    }
    set('channels', value.channels.map((c, idx) => (idx === i ? next : c)));
  };
  const removeChannel = (i: number) =>
    set('channels', value.channels.filter((_, idx) => idx !== i));
  const addChannel = () =>
    set('channels', [
      ...value.channels,
      { icon: 'phone', label: 'Call us', value: '', sub: '', href: '' },
    ]);

  const updateHour = (i: number, patch: Partial<ContactHours>) =>
    set('hours', value.hours.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeHour = (i: number) => set('hours', value.hours.filter((_, idx) => idx !== i));
  const addHour = () => set('hours', [...value.hours, { days: '', time: '' }]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Hero</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Eyebrow" value={value.eyebrow} onChange={(v) => set('eyebrow', v)} />
          <TextField label="Title" value={value.title} onChange={(v) => set('title', v)} />
          <TextArea label="Description" value={value.description} onChange={(v) => set('description', v)} rows={2} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Contact channels ({value.channels.length})</CardTitle>
          <Button size="sm" onClick={addChannel}><Plus className="h-4 w-4" /> Add</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {value.channels.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-500">No contact channels yet.</p>
          )}
          {value.channels.map((c, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={c.icon}
                  onChange={(e) => updateChannel(i, { icon: e.target.value as ContactIcon })}
                  className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm"
                >
                  {CONTACT_ICONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <Input
                  value={c.label}
                  onChange={(e) => updateChannel(i, { label: e.target.value })}
                  placeholder="Label (e.g. Call us)"
                  className="h-8 text-sm w-40"
                />
                <Input
                  value={c.value}
                  onChange={(e) => updateChannel(i, { value: e.target.value })}
                  placeholder={
                    c.icon === 'mail' ? 'name@example.com'
                      : c.icon === 'message-circle' ? '+977 98XXXXXXXX'
                      : c.icon === 'phone' ? '+977 98XXXXXXXX'
                      : 'https://maps.google.com/…'
                  }
                  className="h-8 text-sm flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeChannel(i)}
                  className="p-1 text-slate-400 hover:text-red-500 rounded"
                  aria-label="Remove channel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <Input
                value={c.sub}
                onChange={(e) => updateChannel(i, { sub: e.target.value })}
                placeholder="Subtitle (e.g. Mon – Sun, 9 AM – 9 PM)"
                className="h-8 text-sm"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Message form</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Form heading" value={value.formHeading} onChange={(v) => set('formHeading', v)} />
          <TextArea label="Form intro" value={value.formIntro} onChange={(v) => set('formIntro', v)} rows={2} />
          <TextArea
            label="Subject options (one per line)"
            value={value.formSubjects.join('\n')}
            onChange={(v) => set('formSubjects', v.split('\n').map((s) => s.trim()).filter(Boolean))}
            rows={5}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Office</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Heading" value={value.officeHeading} onChange={(v) => set('officeHeading', v)} />
          <TextArea
            label="Address lines (one per line)"
            value={value.officeLines.join('\n')}
            onChange={(v) => set('officeLines', v.split('\n').map((s) => s.trim()).filter(Boolean))}
            rows={4}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Hours ({value.hours.length})</CardTitle>
          <Button size="sm" onClick={addHour}><Plus className="h-4 w-4" /> Add</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Section heading" value={value.hoursHeading} onChange={(v) => set('hoursHeading', v)} />
          {value.hours.map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={h.days} onChange={(e) => updateHour(i, { days: e.target.value })}
                placeholder="Mon – Fri" className="h-8 text-sm flex-1" />
              <Input value={h.time} onChange={(e) => updateHour(i, { time: e.target.value })}
                placeholder="9 AM – 9 PM" className="h-8 text-sm flex-1" />
              <button type="button" onClick={() => removeHour(i)}
                className="p-1 text-slate-400 hover:text-red-500 rounded">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Returns editor ────────────────────────────────────────────────────────

function ReturnsEditor({ value, onChange }: { value: ReturnsContent; onChange: (next: ReturnsContent) => void }) {
  const set = <K extends keyof ReturnsContent>(k: K, v: ReturnsContent[K]) => onChange({ ...value, [k]: v });

  const updateStep = (i: number, patch: Partial<ReturnsStep>) =>
    set('steps', value.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeStep = (i: number) => set('steps', value.steps.filter((_, idx) => idx !== i));
  const addStep = () => set('steps', [...value.steps, { icon: 'package', label: '', desc: '' }]);
  const moveStep = (from: number, to: number) => {
    if (to < 0 || to >= value.steps.length) return;
    const next = [...value.steps];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    set('steps', next);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Header</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Title" value={value.title} onChange={(v) => set('title', v)} />
          <TextArea label="Intro paragraph" value={value.intro} onChange={(v) => set('intro', v)} rows={2} />
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Window badge" value={value.windowBadge} onChange={(v) => set('windowBadge', v)} placeholder="7-day return window" />
            <TextField label="Window sub" value={value.windowSub} onChange={(v) => set('windowSub', v)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Steps ({value.steps.length})</CardTitle>
          <Button size="sm" onClick={addStep}><Plus className="h-4 w-4" /> Add step</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Section heading" value={value.stepsHeading} onChange={(v) => set('stepsHeading', v)} />
          {value.steps.map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
              <div className="flex items-center gap-2">
                <select value={s.icon} onChange={(e) => updateStep(i, { icon: e.target.value as ReturnsIcon })}
                  className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm">
                  {RETURNS_ICONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Input value={s.label} onChange={(e) => updateStep(i, { label: e.target.value })}
                  placeholder="Step label" className="h-8 text-sm flex-1" />
                <button type="button" onClick={() => moveStep(i, i - 1)} disabled={i === 0}
                  className="p-1 text-slate-400 hover:text-slate-700 rounded disabled:opacity-30">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => moveStep(i, i + 1)} disabled={i === value.steps.length - 1}
                  className="p-1 text-slate-400 hover:text-slate-700 rounded disabled:opacity-30">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => removeStep(i)}
                  className="p-1 text-slate-400 hover:text-red-500 rounded">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <textarea value={s.desc} onChange={(e) => updateStep(i, { desc: e.target.value })} rows={2}
                placeholder="Step description"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm resize-y" />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Non-returnable items</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="Heading" value={value.nonReturnableHeading} onChange={(v) => set('nonReturnableHeading', v)} />
          <TextArea label="Intro / disclaimer" value={value.nonReturnableIntro} onChange={(v) => set('nonReturnableIntro', v)} rows={2} />
          <TextArea
            label="Items (one per line)"
            value={value.nonReturnable.join('\n')}
            onChange={(v) => set('nonReturnable', v.split('\n').map((s) => s.trim()).filter(Boolean))}
            rows={6}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">CTA</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <TextField label="CTA heading" value={value.ctaHeading} onChange={(v) => set('ctaHeading', v)} />
          <TextArea label="CTA sub-text" value={value.ctaSub} onChange={(v) => set('ctaSub', v)} rows={2} />
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="CTA button label" value={value.ctaLabel} onChange={(v) => set('ctaLabel', v)} />
            <TextField label="CTA href" value={value.ctaHref} onChange={(v) => set('ctaHref', v)} placeholder="/account/orders" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

type TabKey = 'contact' | 'privacy' | 'returns' | 'terms';
const TABS: Record<TabKey, { label: string; slug: string }> = {
  contact: { label: 'Contact', slug: 'contact' },
  privacy: { label: 'Privacy Policy', slug: 'privacy' },
  returns: { label: 'Returns', slug: 'returns' },
  terms:   { label: 'Terms of Service', slug: 'terms' },
};
const TAB_ORDER: TabKey[] = ['contact', 'privacy', 'returns', 'terms'];

export function InfoPagesCms() {
  const [data, setData] = useState<InfoPagesContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<TabKey>('contact');

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) setData(await res.json());
      } catch {
        toast.error('Could not reach storefront CMS.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Info pages saved', { description: 'Refresh the storefront to see changes.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const current = TABS[tab];

  const renderEditor = () => {
    if (!data) return null;
    switch (tab) {
      case 'contact':
        return <ContactEditor value={data.contact} onChange={(v) => setData({ ...data, contact: v })} />;
      case 'returns':
        return <ReturnsEditor value={data.returns} onChange={(v) => setData({ ...data, returns: v })} />;
      case 'privacy':
        return <LegalEditor value={data.privacy} onChange={(v) => setData({ ...data, privacy: v })} />;
      case 'terms':
        return <LegalEditor value={data.terms} onChange={(v) => setData({ ...data, terms: v })} />;
    }
  };

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        title="Info Pages CMS"
        description="Edit the Contact, Privacy, Returns, and Terms pages shown on the storefront."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={`${STOREFRONT_URL}/${current.slug}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" /> Open {current.label}
              </a>
            </Button>
            <Button onClick={handleSave} disabled={saving || loading || !data}>
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Publish changes'}
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1 w-fit">
        {TAB_ORDER.map((key) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}>
            {TABS[key].label}
          </button>
        ))}
      </div>

      {loading || !data ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">Loading…</CardContent></Card>
      ) : renderEditor()}
    </div>
  );
}
